import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Section, Card, Badge, Button, Input, Label, Alert, Table } from "../design-system";
import { useApi } from "../data/api";
import { useAuth } from "../auth/AuthProvider";
import { useCart } from "../data/cartStore";
import { discountLabel, discountLabelWithPct } from "../lib/discount_labels";

function money(v) {
  // Formato IT: € 1.234,56 (usato in tutta la summary + shipping-steps).
  const n = Number(v);
  if (!isFinite(n)) return `€ ${v}`;
  return `€ ${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
// Formattazione pallet: 2 decimali max, separatore virgola (IT). Il backend
// resta invariato (Decimal frazionario); la conversione è puramente presentazionale.
function fmtPallets(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// -----------------------------------------------------------------------------
// TryProductsSection — mostrata inline nel carrello (cross-sell prima della
// conferma ordine). Riusa endpoint `/api/customer/try-products` e `useCart.add`
// esistenti. Filtro presentazione: nasconde prodotti già nel carrello (la
// eligibility backend NON viene modificata).
// -----------------------------------------------------------------------------
function TryProductsSection() {
  const { apiGet } = useAuth();
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [added, setAdded] = useState(null);
  const add = useCart((s) => s.add);
  const cartLines = useCart((s) => s.lines);
  useEffect(() => {
    apiGet("/api/customer/try-products").then(setItems).catch(() => setItems([]));
  }, [apiGet]);
  if (!items) return null;
  const cartIds = new Set(cartLines.map((l) => l.product_id));
  const available = items.filter((p) => !cartIds.has(p.id));
  if (available.length === 0) return null;
  const VISIBLE = 6;
  const shown = expanded ? available : available.slice(0, VISIBLE);
  const addWithLayer = (p) => {
    const layer = Number(p.cartons_per_layer) || 0;
    const qty = layer >= 1 ? layer : 1;
    add(p, qty);
    setAdded(p.sku);
    setTimeout(() => setAdded(null), 1500);
  };
  return (
    <Section title="Nuovi prodotti da provare"
             subtitle="Aggiungi una nuova referenza al tuo ordine"
             data-testid="try-products-section">
      <div className="home-grid" data-testid="try-products-grid">
        {shown.map((p) => (
          <Card key={p.id} data-testid={`try-product-${p.sku}`}>
            <Card.Body>
              <div className="kicker">{p.sku}</div>
              <h3 style={{ marginTop: ".2rem", fontSize: ".95rem" }}>{p.name}</h3>
              <div className="muted" style={{ fontSize: ".76rem", minHeight: "1.3em" }}>
                {p.kg_per_carton ? `${p.kg_per_carton} kg/cart.` : "—"}
                {p.pallet_discount_eligible && <> · <span style={{ color: "#b45309" }}>sconto pedana</span></>}
              </div>
              {p.unit_price != null && (
                <div style={{ fontSize: "1rem", fontWeight: 600, marginTop: 4 }}
                     data-testid={`try-price-${p.sku}`}>
                  {money(p.unit_price)}<span style={{ fontSize: ".78rem", opacity: .7 }}> / kg</span>
                </div>
              )}
              <div style={{ marginTop: ".55rem" }}>
                <Button size="sm" style={{ width: "100%" }}
                        data-testid={`try-add-${p.sku}`}
                        onClick={() => addWithLayer(p)}
                        title={p.cartons_per_layer
                          ? `Aggiunge 1 strato = ${p.cartons_per_layer} cartoni`
                          : "Aggiunge 1 cartone"}>
                  {added === p.sku
                    ? `✓ +${p.cartons_per_layer || 1}`
                    : (p.cartons_per_layer ? `+ Strato (${p.cartons_per_layer})` : "+ Aggiungi")}
                </Button>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
      {available.length > VISIBLE && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: ".7rem" }}>
          <Button variant="ghost" size="sm" data-testid="try-see-more"
                  onClick={() => setExpanded(!expanded)}>
            {expanded ? "Mostra meno" : `Scopri altri prodotti (${available.length - VISIBLE})`}
          </Button>
        </div>
      )}
    </Section>
  );
}

// Estrae un messaggio d'errore utile dalla risposta apiPost. Copre:
// - errore stringa singola ({"detail":"..."})
// - errore Pydantic 422 ({"detail":[{loc, msg, ...}, ...]}) → mostra 1° errore leggibile
// - errore application ({"detail":{"code":"...", ...}})
function _pricingErrorMessage(e) {
  const d = e?.data?.detail;
  if (Array.isArray(d) && d.length > 0) {
    const first = d[0];
    const field = Array.isArray(first?.loc) ? first.loc.filter(x => x !== "body").join(".") : "campo";
    // Casi specifici richiesti dall'utente
    if (field === "delivery_point_id") return "Seleziona un punto di consegna";
    if (field === "requested_delivery_date") return "Data di consegna richiesta non valida";
    if (field?.startsWith("lines")) return `Riga carrello non valida: ${first?.msg || ""}`.trim();
    return `Dato non valido (${field}): ${first?.msg || ""}`.trim();
  }
  if (typeof d === "string") return d;
  if (d?.code === "no_price_for_sku" || d?.code === "product_missing_price") {
    return `Prezzo non disponibile per SKU ${d?.sku || d?.product_sku || "?"}`;
  }
  if (d?.code === "advance_payment_not_available") return "Impossibile calcolare lo sconto pagamento anticipato";
  if (d?.detail) return d.detail;
  return e?.message || "Errore pricing";
}

export default function CartPage() {
  const api = useApi();
  const { apiGet, user } = useAuth();
  const cart = useCart();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Feb 2026 — gate v30: quando il backend blocca la creazione ordine perché
  // l'anagrafica non è stata ancora confermata, mostriamo un messaggio dedicato
  // con link a "Il mio account" invece del generico errore di pricing.
  const [verifyBlocked, setVerifyBlocked] = useState(false);
  const [dps, setDps] = useState([]);
  const [pickupLocs, setPickupLocs] = useState([]);
  // v34.31 — Data prevista di consegna calcolata dal backend, usata come
  // FLOOR sul date-picker "Data richiesta di consegna" per impedire al
  // Customer di richiedere una consegna precedente. Hoisted qui così è
  // condivisa fra la card di stima e il campo requested-date.
  const [estimatedDate, setEstimatedDate] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.myDeliveryPoints().then(setDps).catch(() => {}); }, []);
  useEffect(() => {
    // Feb 2026 (P0 auth session fix): usa client centralizzato apiGet
    // (silent refresh su 401) invece del raw fetch che bypassava AuthProvider.
    apiGet("/api/pickup-locations").then(setPickupLocs).catch(() => setPickupLocs([]));
  }, [apiGet]);
  // Auto-select the single active pickup location when user switches to PICKUP.
  useEffect(() => {
    if (cart.deliveryMode === "PICKUP" && pickupLocs.length === 1 && !cart.pickupRuleId) {
      cart.setMeta({ pickupRuleId: pickupLocs[0].id });
    }
  }, [cart.deliveryMode, pickupLocs]);

  const runPreview = async () => {
    if (cart.lines.length === 0) return;
    setLoading(true); setErr(null);
    try {
      // Perf fix (2026-02): usa user.customer_id già disponibile in useAuth
      // per evitare la round-trip a /api/customers (~1s Neon RTT).
      const meId = user?.customer_id || (await api.myCustomer())[0]?.id;
      const body = {
        customer_id: meId,
        delivery_mode: cart.deliveryMode,
        delivery_point_id: cart.deliveryPointId || null,
        advance_payment: cart.advancePayment,
        // Bug fix (2026-02): il backend Pydantic rifiuta "" come date con 422.
        // cart.requestedDate può essere "" (se il datepicker viene svuotato) → forza null.
        requested_delivery_date: cart.requestedDate ? cart.requestedDate : null,
        lines: cart.lines.map((l) => ({ product_id: l.product_id, qty_cartons: l.qty_cartons })),
      };
      setPreview(await api.previewCart(body));
    } catch (e) {
      setErr(_pricingErrorMessage(e));
    } finally { setLoading(false); }
  };
  // Debounce preview requests (300ms) — kg totali/cartoni sono aggiornati
  // immediatamente lato UI, la preview rifresca prezzi/sconti/trasporto.
  const previewTimer = useRef(null);
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    if (cart.lines.length === 0) { setPreview(null); return; }
    previewTimer.current = setTimeout(runPreview, 300);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [cart.lines, cart.deliveryMode, cart.advancePayment, cart.deliveryPointId, cart.requestedDate]);

  // Kg totali calcolati LOCALMENTE per aggiornamento immediato (no wait su preview)
  const localKgTotal = useMemo(
    () => cart.lines.reduce((s, l) => s + (Number(l.qty_cartons) || 0) * (Number(l.kg_per_carton) || 0), 0),
    [cart.lines],
  );

  const [submitting, setSubmitting] = useState(false); // Feb 2026 — guard doppio click Conferma ordine
  const confirmOrder = async () => {
    // Guard front-end (Feb 2026): impedisce doppie chiamate all'API su doppio
    // click rapido. Il bottone è anche disabilitato visualmente, ma il guard
    // qui protegge contro race conditions su eventi trigger multipli.
    if (submitting) return;
    setSubmitting(true);
    setErr(null);
    setVerifyBlocked(false);
    try {
      const body = {
        delivery_mode: cart.deliveryMode,
        delivery_point_id: cart.deliveryPointId || null,
        pickup_rule_id: cart.deliveryMode === "PICKUP" ? (cart.pickupRuleId || null) : null,
        advance_payment: cart.advancePayment,
        requested_delivery_date: cart.requestedDate ? cart.requestedDate : null,
        lines: cart.lines.map((l) => ({ product_id: l.product_id, qty_cartons: l.qty_cartons })),
        notes_customer: cart.notes,
      };
      // Re-preview to check hash freshness. Perf fix (2026-02): usa
      // user.customer_id da useAuth invece di /api/customers round-trip.
      const meId = user?.customer_id || (await api.myCustomer())[0]?.id;
      const fresh = await api.previewCart({ ...body, customer_id: meId });
      if (preview && preview.pricing_result_hash !== fresh.pricing_result_hash) {
        setPreview(fresh);
        setErr("Il prezzo è cambiato dall'ultimo calcolo. Verifica il nuovo totale prima di confermare.");
        return;
      }
      const created = await api.createOrder(body);
      cart.clear();
      nav(`/customer/orders/${created.id}`);
    } catch (e) {
      // Gate v30: intercetta 409 CUSTOMER_PROFILE_VERIFICATION_REQUIRED e mostra
      // messaggio user-friendly con CTA verso "Il mio account" (nessun errore raw).
      const code = e?.data?.detail?.code;
      if (code === "CUSTOMER_PROFILE_VERIFICATION_REQUIRED") {
        setVerifyBlocked(true);
      } else if (code === "REQUESTED_DELIVERY_DATE_BEFORE_ESTIMATED") {
        // v42 (Mar 2026) — backend gate: la data richiesta è precedente alla
        // data prevista di consegna canonica. Il frontend ha già il floor,
        // ma se un client bypassa (paste, click rapido, API diretta) il
        // backend rifiuta e mostriamo un messaggio chiaro con la data prevista.
        const est = e?.data?.detail?.estimated_delivery_date;
        const estFmt = est ? new Date(est).toLocaleDateString("it-IT") : null;
        setErr(
          estFmt
            ? `La data richiesta non può essere precedente alla data prevista di consegna (${estFmt}).`
            : "La data richiesta non può essere precedente alla data prevista di consegna."
        );
      } else {
        setErr(_pricingErrorMessage(e) || "Errore conferma ordine");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const totalCartons = cart.lines.reduce((s, l) => s + l.qty_cartons, 0);

  return (
    <>
      <Section title="Carrello" subtitle={`${cart.lines.length} articoli · ${totalCartons} cartoni · ${localKgTotal.toFixed(1)} kg`}>
        <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }}>
          <Button data-testid="cart-back-to-catalog" variant="outline" size="sm"
                  onClick={() => nav("/customer/catalog")}>
            ← Torna al catalogo
          </Button>
        </div>
        {cart.lines.length === 0 && <Alert tone="info">Il tuo carrello è vuoto. Vai al catalogo per aggiungere prodotti.</Alert>}
      </Section>

      {cart.lines.length > 0 && (
        <>
          <Section title="Prodotti">
            <Table
              columns={[
                { key: "sku", label: "SKU" },
                { key: "name", label: "Prodotto" },
                { key: "qty", label: "Cartoni", render: (r) => {
                  const layer = Number(r.cartons_per_layer) || 0;
                  const step = layer >= 1 ? layer : 1;
                  const nextUp = layer >= 1 ? Math.ceil((r.qty_cartons + 1) / layer) * layer : r.qty_cartons + 1;
                  const notLayerMultiple = layer >= 1 && (r.qty_cartons % layer !== 0);
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <div style={{ display: "flex", gap: ".25rem", alignItems: "center" }}>
                        <Button data-testid={`qty-minus-${r.sku}`} size="sm" variant="outline"
                                onClick={() => cart.setQty(r.product_id, Math.max(1, r.qty_cartons - 1))}
                                title="−1 cartone">−</Button>
                        <input data-testid={`qty-${r.sku}`}
                               type="number" min="1"
                               style={{ width: 60, textAlign: "center", padding: "2px 4px" }}
                               value={r.qty_cartons}
                               onChange={(e) => {
                                 const v = parseInt(e.target.value, 10);
                                 if (Number.isInteger(v) && v >= 1) cart.setQty(r.product_id, v);
                               }} />
                        <Button data-testid={`qty-plus-${r.sku}`} size="sm" variant="outline"
                                onClick={() => cart.setQty(r.product_id, r.qty_cartons + 1)}
                                title="+1 cartone">+</Button>
                      </div>
                      {notLayerMultiple && (
                        <div className="muted" data-testid={`qty-hint-layer-${r.sku}`} style={{ fontSize: ".72rem" }}>
                          Completa lo strato con altri {layer - (r.qty_cartons % layer)} cartoni (strato = {layer})
                        </div>
                      )}
                    </div>
                  );
                }},
                { key: "kg", label: "kg", render: (r) => Number(r.kg_per_carton * r.qty_cartons).toFixed(1) },
                // €/kg — valore backend `unit_price_effective` (€/kg) letto dal preview.
                // Non ricalcoliamo prezzi lato FE: se il preview è in flight o mancante
                // per una riga (es. add appena avvenuto), mostriamo "—".
                { key: "eur_kg", label: "€/kg", render: (r) => {
                  const pl = (preview?.lines || []).find(x => x.product_id === r.product_id);
                  return pl?.unit_price_effective != null
                    ? <span data-testid={`cart-eurkg-${r.sku}`}>{money(pl.unit_price_effective)}</span>
                    : <span className="muted">—</span>;
                }},
                // Totale riga — valore backend `line_total` (già scontato se applicabile).
                // Se la riga ha promo applicate (contract o general, escluso PALLET_MONO che
                // ha già la sua label dedicata), mostro badge promo + sconto riga + subtotale
                // originario barrato per rendere trasparente il calcolo.
                { key: "line_total", label: "Totale riga", render: (r) => {
                  const pl = (preview?.lines || []).find(x => x.product_id === r.product_id);
                  if (pl?.line_total == null) return <span className="muted">—</span>;
                  const promos = pl.applied_promotions || [];
                  const monoLine = promos.find(p => (p?.type || "").toString().toUpperCase() === "PALLET_MONO");
                  const contract = pl.applied_contract_promo || null;
                  const otherGen = promos.find(p => {
                    const t = (p?.type || "").toString().toUpperCase();
                    return t && t !== "PALLET_MONO" && t !== "CONTRACT_PROMO";
                  });
                  const promoAmount = Number(pl.line_discount || 0)
                    - Number(monoLine?.amount || 0);
                  const hasRowPromo = (contract && Number(contract.amount) > 0)
                    || (otherGen && Number(otherGen.amount) > 0)
                    || promoAmount > 0;
                  const subtotalBefore = pl.line_subtotal_before_discount != null
                    ? Number(pl.line_subtotal_before_discount)
                    : null;
                  const promoLabel = () => {
                    const src = contract || otherGen;
                    if (!src) return "Promozione";
                    // v33 — Nome commerciale reale della promo (backend snapshot),
                    // + %/importo per trasparenza.
                    const commercialName = (src.name && String(src.name).trim())
                      || (contract && src.contract_name)
                      || (contract ? "Promozione contratto" : "Promozione");
                    const dt = (src.discount_type || "").toUpperCase();
                    if (dt === "PERCENT") {
                      const pct = Number(src.value);
                      const pctFmt = Number.isFinite(pct)
                        ? pct.toFixed(pct % 1 ? 2 : 0)
                        : String(src.value);
                      return `${commercialName} · -${pctFmt}%`;
                    }
                    return commercialName;
                  };
                  return (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                      <strong data-testid={`cart-linetotal-${r.sku}`}>{money(pl.line_total)}</strong>
                      {hasRowPromo && subtotalBefore != null && Math.abs(subtotalBefore - Number(pl.line_total)) > 0.001 && (
                        <span data-testid={`cart-line-subtotal-orig-${r.sku}`}
                              className="muted"
                              style={{ fontSize: ".72rem", textDecoration: "line-through", opacity: .7 }}>
                          da {money(subtotalBefore)}
                        </span>
                      )}
                      {hasRowPromo && promoAmount > 0 && (
                        <span data-testid={`cart-promo-badge-${r.sku}`}
                              style={{
                                fontSize: ".72rem", color: "#0F7B4A", fontWeight: 600,
                                background: "#E6F5EE", border: "1px solid #B7E1CD",
                                borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap",
                              }}>
                          {promoLabel()}
                        </span>
                      )}
                      {hasRowPromo && promoAmount > 0 && (
                        <span data-testid={`cart-promo-saving-${r.sku}`}
                              style={{ fontSize: ".72rem", color: "#0F7B4A" }}>
                          Sconto riga: -{money(promoAmount)}
                        </span>
                      )}
                      {monoLine && Number(monoLine.amount) > 0 && (
                        <span data-testid={`cart-mono-saving-${r.sku}`}
                              className="muted" style={{ fontSize: ".72rem", color: "#0F7B4A" }}>
                          Risparmio pallet mono: -{money(monoLine.amount)}
                        </span>
                      )}
                    </div>
                  );
                }},
                { key: "rm", label: "", render: (r) => (
                  <Button data-testid={`remove-${r.sku}`} size="sm" variant="ghost" onClick={() => cart.remove(r.product_id)}>Rimuovi</Button>
                )},
              ]}
              rows={cart.lines}
            />
          </Section>

          <TryProductsSection />

          <Section title="Consegna & pagamento">
            <Card>
              <Card.Body>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "1rem" }}>
                  <div>
                    <Label>Modalità</Label>
                    <select data-testid="delivery-mode" className="ds-input"
                            value={cart.deliveryMode}
                            onChange={(e) => cart.setMeta({ deliveryMode: e.target.value })}>
                      <option value="DELIVERY">Consegna a domicilio</option>
                      <option value="PICKUP">Ritiro presso deposito</option>
                    </select>
                  </div>
                  {cart.deliveryMode === "DELIVERY" && (
                    <div>
                      <Label>Punto di consegna</Label>
                      <select data-testid="delivery-point" className="ds-input"
                              value={cart.deliveryPointId || ""}
                              onChange={(e) => cart.setMeta({ deliveryPointId: e.target.value || null })}>
                        <option value="">— seleziona —</option>
                        {dps.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.city} ({d.province})</option>)}
                      </select>
                    </div>
                  )}
                  {cart.deliveryMode === "PICKUP" && pickupLocs.length > 0 && (
                    <div style={{ gridColumn: "1 / -1" }} data-testid="pickup-location-block">
                      <Label>Sede di ritiro{pickupLocs.length > 1 ? " *" : ""}</Label>
                      {pickupLocs.length === 1 ? (
                        <Card style={{ background: "#F3F6FA" }} data-testid="pickup-location-fixed">
                          <Card.Body style={{ padding: 12 }}>
                            <div style={{ fontWeight: 600 }}>{pickupLocs[0].warehouse_name}</div>
                            {pickupLocs[0].address && <div>{pickupLocs[0].address}</div>}
                            {(pickupLocs[0].zip_code || pickupLocs[0].city) && (
                              <div>
                                {pickupLocs[0].zip_code} {pickupLocs[0].city}
                                {pickupLocs[0].province ? ` (${pickupLocs[0].province})` : ""}
                              </div>
                            )}
                            {pickupLocs[0].phone && <div>Tel. {pickupLocs[0].phone}</div>}
                            {pickupLocs[0].pickup_hours && (
                              <div className="muted" style={{ fontSize: ".85rem", marginTop: 4 }}>
                                Orari ritiro: {pickupLocs[0].pickup_hours}
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      ) : (
                        <div data-testid="pickup-location-choices"
                             style={{ display: "grid", gap: 8 }}>
                          {pickupLocs.map((p) => {
                            const selected = cart.pickupRuleId === p.id;
                            return (
                              <label key={p.id}
                                     data-testid={`pickup-loc-${p.id}`}
                                     style={{
                                       display: "block",
                                       border: selected ? "2px solid #0F4C81" : "1px solid #DBE3EE",
                                       borderRadius: 8,
                                       padding: 12,
                                       background: selected ? "#F3F6FA" : "#fff",
                                       cursor: "pointer",
                                     }}>
                                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                                  <input type="radio" name="pickup-loc" value={p.id}
                                         checked={selected}
                                         onChange={() => cart.setMeta({ pickupRuleId: p.id })}
                                         style={{ marginTop: 4 }} />
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600 }}>{p.warehouse_name}</div>
                                    {p.address && <div>{p.address}</div>}
                                    {(p.zip_code || p.city) && (
                                      <div>
                                        {p.zip_code} {p.city}
                                        {p.province ? ` (${p.province})` : ""}
                                      </div>
                                    )}
                                    {p.phone && <div style={{ fontSize: ".9rem" }}>Tel. {p.phone}</div>}
                                    {p.pickup_hours && (
                                      <div className="muted" style={{ fontSize: ".85rem", marginTop: 4 }}>
                                        Orari ritiro: {p.pickup_hours}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                          {!cart.pickupRuleId && (
                            <div className="muted" style={{ fontSize: ".85rem", color: "#b45309" }}>
                              Seleziona una sede di ritiro prima di confermare l'ordine.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <Label>Data richiesta</Label>
                    <Input data-testid="requested-date" type="date"
                           min={estimatedDate || undefined}
                           value={cart.requestedDate || ""}
                           onChange={(e) => {
                             const v = e.target.value || null;
                             if (v && estimatedDate && v < estimatedDate) {
                               // Silently clamp — the date picker min= should
                               // already prevent this, but guard against paste
                               // or programmatic input.
                               cart.setMeta({ requestedDate: estimatedDate });
                               return;
                             }
                             cart.setMeta({ requestedDate: v });
                           }} />
                    {estimatedDate && (
                      <div className="muted" style={{ fontSize: ".85rem", marginTop: 4 }}
                           data-testid="requested-date-floor-hint">
                        Puoi richiedere una consegna a partire dal{" "}
                        {new Date(estimatedDate).toLocaleDateString("it-IT")}.
                      </div>
                    )}
                    {cart.requestedDate && estimatedDate && cart.requestedDate < estimatedDate && (
                      <div style={{ color: "#b91c1c", fontSize: ".9rem", marginTop: 4 }}
                           data-testid="requested-date-error">
                        La data richiesta non può essere precedente alla data prevista di
                        consegna ({new Date(estimatedDate).toLocaleDateString("it-IT")}).
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <label style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                      <input data-testid="advance-payment" type="checkbox"
                             checked={cart.advancePayment}
                             onChange={(e) => cart.setMeta({ advancePayment: e.target.checked })} />
                      Pagamento anticipato (con eventuale sconto)
                    </label>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Label>Note ordine</Label>
                    <Input data-testid="notes" placeholder="Note per la consegna, referente, ecc."
                           value={cart.notes}
                           onChange={(e) => cart.setMeta({ notes: e.target.value })} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Section>

          {/* Mar 2026 v36 — Data prevista di consegna cliente.
              Read-only. Nessun dato interno vettore/costo. */}
          {cart.deliveryMode === "DELIVERY" && cart.deliveryPointId && (
            <Section title="Data prevista di consegna">
              <CustomerDeliveryEstimateCard
                deliveryPointId={cart.deliveryPointId}
                estimatedPallets={preview?.estimated_pallets}
                totalKg={localKgTotal}
                onEstimated={setEstimatedDate}
              />
            </Section>
          )}

          <Section title="Riepilogo ordine">
            {loading && <Card><Card.Body className="muted">Ricalcolo…</Card.Body></Card>}
            {err && <Alert tone="warning">{err}</Alert>}
            {verifyBlocked && (
              <Alert tone="warning" data-testid="cart-verify-profile-required">
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: "1rem" }}>
                    Per inviare l'ordine devi prima confermare i dati della tua azienda.
                  </div>
                  <div>
                    <Link
                      to="/customer/account"
                      data-testid="cart-verify-profile-link"
                      style={{
                        display: "inline-block",
                        background: "#B45309", color: "#fff",
                        padding: "10px 18px", borderRadius: 8,
                        fontWeight: 700, textDecoration: "none",
                      }}
                    >
                      VAI ALLA VERIFICA DATI
                    </Link>
                  </div>
                </div>
              </Alert>
            )}
            {preview && (
              <Card data-testid="pricing-summary">
                <Card.Body>
                  <div className="stack-2">
                    <Row label="Subtotale merce">{money(preview.subtotal)}</Row>
                    {/* Righe sconto customer-friendly, aggregate dai top-level
                        applied_discounts del pricing engine (mai codici enum). */}
                    {(preview.applied_discounts || []).map((a, i) => (
                      <Row key={`ad-${i}`} label={<span data-testid={`row-discount-${a.type}`}>{discountLabelWithPct(a)}</span>}>
                        -{money(a.amount)}
                      </Row>
                    ))}
                    <Row label={cart.deliveryMode === "PICKUP" ? "Trasporto (ritiro)" : "Trasporto"}>
                      {money(preview.shipping_cost)}
                    </Row>
                    <Row label={<strong>Totale</strong>}><strong style={{ fontSize: "1.4rem" }}>{money(preview.total)}</strong></Row>
                    <div className="muted" style={{ fontSize: ".85rem" }} data-testid="summary-totals">
                      Kg totali: <strong>{localKgTotal.toFixed(1)}</strong>
                      {loading && <span style={{ marginLeft: 8, opacity: .6 }}>· ricalcolo prezzi…</span>}
                      {preview?.estimated_pallets != null && <> · Pallet stimati: <span data-testid="cart-estimated-pallets">{fmtPallets(preview.estimated_pallets)}</span></>}
                    </div>
                    {/* Hint "aggiungi X cartoni per pedana completa" — mostrato se qualche riga è vicina a un pallet completo E il prodotto è pallet_discount_eligible */}
                    {(preview.lines || []).map(l => {
                      const line = cart.lines.find(x => x.product_id === l.product_id);
                      if (!line || !line.cartons_per_std_pallet || !line.pallet_discount_eligible) return null;
                      const cps = Number(line.cartons_per_std_pallet);
                      const missing = cps - (l.qty_cartons % cps);
                      if (missing === cps || missing > cps * 0.4) return null;
                      return (
                        <Alert tone="info" key={l.product_id}
                               data-testid={`near-pallet-hint-${line.sku}`}>
                          <strong>{line.sku}</strong>: aggiungi altri {missing} cartoni
                          per completare la pedana e attivare lo sconto monoreferenza.
                        </Alert>
                      );
                    })}
                    {Number(preview.free_shipping_missing) > 0 && (!preview.shipping_steps || preview.shipping_steps.length === 0) &&
                      <Alert tone="info">Ti mancano {money(preview.free_shipping_missing)} per il trasporto gratuito.</Alert>}
                    {Array.isArray(preview.shipping_steps) && preview.shipping_steps.length > 0 && cart.deliveryMode === "DELIVERY" && (
                      <div data-testid="shipping-steps"
                           style={{ background: "#F3F6FA", border: "1px solid #DBE3EE", borderRadius: 8,
                                    padding: "10px 12px", marginTop: 8 }}>
                        <div style={{ fontSize: ".82rem", color: "#0F4C81", marginBottom: 6 }}>
                          <strong>Contributo trasporto attuale: {money(preview.shipping_cost)}</strong>
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
                          {preview.shipping_steps.map((s, i) => (
                            <li key={i}
                                data-testid={`shipping-step-${i}`}
                                style={{
                                  padding: "4px 0",
                                  fontSize: i === 0 ? ".9rem" : ".82rem",
                                  fontWeight: i === 0 ? 600 : 400,
                                  color: s.is_free_shipping ? "#0F7B4A" : (i === 0 ? "#0F4C81" : "#374151"),
                                }}>
                              Ti mancano <strong>{money(s.amount_missing)}</strong>
                              {s.is_free_shipping
                                ? " per il trasporto gratuito"
                                : <> per scendere a <strong>{money(s.shipping_contribution)}</strong></>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {Number(preview.pfa_potential_kg) > 0 && preview.pfa_projection?.targets?.length > 0 && (
                      <Alert tone="success">
                        Questo ordine contribuisce per <strong>{Number(preview.pfa_potential_kg).toFixed(1)} kg</strong> al tuo PFA.
                        Proiezione: <strong>{preview.pfa_projection.targets[0].projected_pct}%</strong> del target.
                      </Alert>
                    )}
                  </div>
                </Card.Body>
                <Card.Footer>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
                    <Button data-testid="cart-continue-shopping" variant="ghost" size="md"
                            onClick={() => nav("/customer/catalog")}>
                      ← Continua ad aggiungere prodotti
                    </Button>
                    <Button data-testid="confirm-order" variant="primary" size="lg"
                            onClick={confirmOrder}
                            loading={submitting} disabled={submitting || (cart.deliveryMode === "PICKUP" && pickupLocs.length > 1 && !cart.pickupRuleId)}
                            title={submitting
                              ? "Conferma in corso, attendi…"
                              : (cart.deliveryMode === "PICKUP" && pickupLocs.length > 1 && !cart.pickupRuleId
                                  ? "Seleziona una sede di ritiro prima di confermare l'ordine."
                                  : undefined)}>
                      {submitting ? (
                        <span data-testid="confirm-order-submitting"
                              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <span aria-hidden="true"
                                style={{ width: 14, height: 14, borderRadius: "50%",
                                         border: "2px solid rgba(255,255,255,0.4)",
                                         borderTopColor: "#fff",
                                         animation: "cart-spin 0.7s linear infinite",
                                         display: "inline-block" }} />
                          Invio ordine…
                        </span>
                      ) : "Conferma ordine"}
                    </Button>
                    <style>{`@keyframes cart-spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                </Card.Footer>
              </Card>
            )}
          </Section>

          {preview?.lines?.some(l => l.line_kind === "FREE") && (
            <Section title="🎁 Cartoni omaggio (BUY X + Y)">
              <Card data-testid="free-cartons-section"><Card.Body>
                <Alert tone="success">
                  Il tuo ordine include cartoni omaggio grazie a promozioni attive.
                  Sono <strong>gratuiti (€0)</strong>, contribuiscono al peso spedito ma non al PFA.
                </Alert>
                <ul style={{ margin: "8px 0 0 0", paddingLeft: "1.2rem" }}>
                  {preview.lines.filter(l => l.line_kind === "FREE").map((l, i) => {
                    const gs = l.free_gift_snapshot || {};
                    return (
                      <li key={i} data-testid={`free-line-${i}`}>
                        <strong>{gs.gift_product_sku || "SKU"}</strong> · {l.qty_cartons} cartoni ×{" "}
                        <span className="muted">omaggio da promo</span>{" "}
                        <code>{gs.promotion_code || gs.contract_code}</code>
                        {" "}({gs.buy_cartons}+{gs.free_cartons})
                        {" · "} <span className="muted">{Number(l.line_weight_kg).toFixed(1)} kg</span>
                      </li>
                    );
                  })}
                </ul>
              </Card.Body></Card>
            </Section>
          )}

          {preview?.applied_discounts?.length > 0 && (
            <Section title="Sconti applicati">
              <Card><Card.Body>
                <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                  {preview.applied_discounts.map((a, i) => (
                    <li key={i}><strong>{discountLabel(a)}</strong>: -{money(a.amount)}</li>
                  ))}
                </ul>
              </Card.Body></Card>
            </Section>
          )}
        </>
      )}
    </>
  );
}

function Row({ label, children }) {
  return <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}><span>{label}</span><span>{children}</span></div>;
}

// ---------------------------------------------------------------------------
// Mar 2026 v36 — Customer-facing delivery estimate card for checkout.
// Reads from POST /api/customer/delivery-estimate (backend: cheapest eligible
// carrier + admin loading config + Delivery Estimate Engine). NEVER shows
// carrier name/cost/reason. Debounced 300ms on DP/pallets changes.
// ---------------------------------------------------------------------------
function CustomerDeliveryEstimateCard({ deliveryPointId, estimatedPallets, totalKg, onEstimated }) {
  const { apiPost } = useAuth();
  const [state, setState] = useState({ loading: true, date: null, available: false });
  const timer = useRef(null);
  useEffect(() => {
    if (!deliveryPointId) { setState({ loading: false, date: null, available: false }); if (onEstimated) onEstimated(null); return; }
    if (timer.current) clearTimeout(timer.current);
    setState((s) => ({ ...s, loading: true }));
    timer.current = setTimeout(async () => {
      try {
        const r = await apiPost("/api/customer/delivery-estimate", {
          delivery_point_id: deliveryPointId,
          pallets: Math.max(1, Math.ceil(Number(estimatedPallets) || 1)),
          kg: Number(totalKg) || 0,
        });
        const d = r?.estimated_delivery_date || null;
        setState({ loading: false, date: d, available: !!r?.available });
        if (onEstimated) onEstimated(d);
      } catch {
        setState({ loading: false, date: null, available: false });
        if (onEstimated) onEstimated(null);
      }
    }, 300);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryPointId, estimatedPallets]);

  if (state.loading) {
    return (
      <Card data-testid="checkout-delivery-estimate-loading">
        <Card.Body className="muted">Calcolo data prevista di consegna…</Card.Body>
      </Card>
    );
  }
  if (state.available && state.date) {
    return (
      <Card data-testid="checkout-delivery-estimate-available">
        <Card.Body>
          <div style={{ fontSize: "1.1rem", fontWeight: 600, color: "#0F4C81" }}
               data-testid="checkout-delivery-date">
            Data prevista di consegna: {new Date(state.date).toLocaleDateString("it-IT")}
          </div>
          <div className="muted" style={{ marginTop: 6, fontSize: ".88rem" }}
               data-testid="checkout-delivery-disclaimer">
            Data provvisoria, valida in caso di completa disponibilità dei prodotti.
            L'ufficio logistico ti contatterà dopo l'inserimento dell'ordine nel
            gestionale.
          </div>
        </Card.Body>
      </Card>
    );
  }
  return (
    <Card data-testid="checkout-delivery-estimate-unavailable">
      <Card.Body>
        <div className="muted" style={{ fontSize: ".95rem" }}
             data-testid="checkout-delivery-unavailable-msg">
          La data prevista di consegna non è al momento disponibile.
          L'ufficio logistico ti contatterà dopo l'inserimento dell'ordine
          nel gestionale per la pianificazione della consegna.
        </div>
      </Card.Body>
    </Card>
  );
}
